import { useEffect, useState } from "react";
import { http, money, fmtDate, formatApiError, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Trash2, FileText, Plus, Edit3 } from "lucide-react";
import { toast } from "sonner";

export const LavanderiaDocs = ({ year, month, apartments = [] }) => {
  const [docs, setDocs] = useState([]);
  const [cmp, setCmp] = useState(null);
  const [kind, setKind] = useState("ddt");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);   // anteprima estratta, correggibile
  const [editId, setEditId] = useState(null);

  const load = async () => {
    if (!year || !month) { setDocs([]); setCmp(null); return; }
    const [d, c] = await Promise.all([
      http.get("/laundry-docs", { params: { year, month } }),
      http.get("/laundry-compare", { params: { year, month } }),
    ]);
    setDocs(d.data); setCmp(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const analyze = async () => {
    if (!file) return toast.error("Seleziona il PDF");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
      const { data } = await http.post("/laundry-docs/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft({ ...data, apartment_id: "", note: "", stato: "da_verificare", data: data.data || new Date().toISOString().slice(0, 10) });
      setEditId(null); setFile(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const setRow = (i, k, v) => { const rows = [...draft.rows]; rows[i] = { ...rows[i], [k]: k === "articolo" ? v : (v === "" ? null : Number(v)) }; setDraft({ ...draft, rows }); };
  const addRow = () => setDraft({ ...draft, rows: [...(draft.rows || []), { articolo: "", quantita: 0, prezzo: null, importo: null }] });
  const delRow = (i) => setDraft({ ...draft, rows: draft.rows.filter((_, j) => j !== i) });

  const save = async () => {
    const payload = { kind: draft.kind, numero: draft.numero || null, data: draft.data, year, month,
      apartment_id: draft.apartment_id || null, file_id: draft.file_id || null, filename: draft.filename || null,
      rows: (draft.rows || []).filter((r) => r.articolo), note: draft.note || null, stato: draft.stato || "da_verificare" };
    try {
      if (editId) await http.put(`/laundry-docs/${editId}`, payload); else await http.post("/laundry-docs", payload);
      toast.success(draft.kind === "ddt" ? "DDT salvato" : "Fattura salvata"); setDraft(null); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (d) => {
    if (!window.confirm(`Sei sicuro di voler eliminare questo ${d.kind === "ddt" ? "DDT" : "documento"}? L'operazione non modifica nessun rendiconto.`)) return;
    await http.delete(`/laundry-docs/${d.id}`); toast.success("Eliminato"); load();
  };
  const aptName = (id) => apartments.find((a) => a.id === id)?.name || "—";

  return (
    <div className="space-y-4" data-testid="lavanderia-docs">
      <Card className="p-4 rounded-2xl">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="font-display text-lg font-bold text-[var(--teal-deep)]">DDT e fattura lavanderia</div>
            <div className="text-xs text-slate-500">Strumento di controllo: i dati estratti sono correggibili e non modificano rendiconti o importi proprietari.</div>
          </div>
          <div className="ml-auto flex flex-wrap items-end gap-2">
            <div><Label>Tipo</Label>
              <Select value={kind} onValueChange={setKind}><SelectTrigger className="w-[150px]" data-testid="ddt-kind"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ddt">DDT</SelectItem><SelectItem value="fattura">Fattura mensile</SelectItem></SelectContent></Select>
            </div>
            <div><Label>PDF</Label><Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ddt-file" className="w-[220px]" /></div>
            <Button onClick={analyze} disabled={busy} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]" data-testid="ddt-analyze"><Upload className="h-4 w-4 mr-1" />{busy ? "Leggo..." : "Leggi PDF"}</Button>
          </div>
        </div>
      </Card>

      {docs.length > 0 && (
        <Card className="rounded-2xl overflow-hidden" data-testid="ddt-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr><th className="text-left p-3">Tipo</th><th className="text-left">Data</th><th className="text-left">Numero</th><th className="text-left">Appartamento</th><th className="text-right">Pezzi</th><th className="text-right">Importo</th><th></th></tr></thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className="border-t border-slate-100" data-testid={`ddt-${d.id}`}>
                  <td className="p-3"><Badge variant="outline" className={d.kind === "ddt" ? "border-slate-300" : "border-teal-300 text-teal-700"}>{d.kind === "ddt" ? "DDT" : "Fattura"}</Badge></td>
                  <td>{fmtDate(d.data)}</td>
                  <td>{d.numero || "—"}</td>
                  <td>{d.apartment_id ? aptName(d.apartment_id) : "—"}</td>
                  <td className="text-right tabular-nums">{d.totale_pezzi}</td>
                  <td className="text-right tabular-nums">{d.totale_importo ? money(d.totale_importo) : "—"}</td>
                  <td className="text-right whitespace-nowrap pr-3">
                    {d.file_id && <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="p-2 inline-block text-slate-500 hover:text-teal-700" data-testid={`ddt-pdf-${d.id}`}><FileText className="h-4 w-4" /></a>}
                    <button onClick={() => { setDraft({ ...d }); setEditId(d.id); }} className="p-2 text-slate-500 hover:text-teal-700" data-testid={`ddt-edit-${d.id}`}><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => del(d)} className="p-2 text-slate-500 hover:text-rose-600" data-testid={`ddt-del-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {cmp && cmp.rows.length > 0 && (
        <Card className="rounded-2xl overflow-hidden" data-testid="ddt-compare">
          <div className="p-4 pb-2">
            <div className="font-display font-bold text-[var(--teal-deep)]">Confronto DDT ↔ fattura</div>
            <div className="text-xs text-slate-500">{cmp.ddt_count} DDT · {cmp.fatture_count} fatture · pezzi DDT {cmp.totale_pezzi_ddt} vs fattura {cmp.totale_pezzi_fattura} · {cmp.differenze} articoli da verificare</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr><th className="text-left p-3">Articolo</th><th className="text-right">Q.tà DDT</th><th className="text-right">Q.tà fattura</th><th className="text-right">Differenza</th><th className="text-right">Diff. importo</th><th className="text-left pl-4">Esito</th></tr></thead>
            <tbody>
              {cmp.rows.map((r) => (
                <tr key={r.articolo} className="border-t border-slate-100" data-testid={`cmp-${r.articolo}`}>
                  <td className="p-3">{r.articolo}</td>
                  <td className="text-right tabular-nums">{r.qta_ddt}</td>
                  <td className="text-right tabular-nums">{r.qta_fattura}</td>
                  <td className={`text-right tabular-nums font-semibold ${r.differenza === 0 ? "text-slate-400" : r.differenza > 0 ? "text-rose-700" : "text-amber-700"}`}>{r.differenza > 0 ? `+${r.differenza}` : r.differenza}</td>
                  <td className="text-right tabular-nums text-slate-500">{r.differenza_importo ? money(r.differenza_importo) : "—"}</td>
                  <td className="pl-4"><Badge variant="outline" className={r.esito === "ok" ? "border-emerald-300 text-emerald-700" : "border-amber-400 text-amber-800"}>{r.esito === "ok" ? "OK" : "DA VERIFICARE"}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={!!draft} onOpenChange={() => setDraft(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ddt-preview">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Modifica" : "Anteprima"} {draft?.kind === "ddt" ? "DDT" : "fattura"} lavanderia</DialogTitle></DialogHeader>
          {draft && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label>Data</Label><Input type="date" value={draft.data || ""} onChange={(e) => setDraft({ ...draft, data: e.target.value })} data-testid="ddt-date" /></div>
                <div><Label>Numero</Label><Input value={draft.numero || ""} onChange={(e) => setDraft({ ...draft, numero: e.target.value })} data-testid="ddt-numero" /></div>
                <div><Label>Appartamento</Label>
                  <Select value={draft.apartment_id || "none"} onValueChange={(v) => setDraft({ ...draft, apartment_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="ddt-apt"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Non associato</SelectItem>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label>Stato</Label>
                  <Select value={draft.stato || "da_verificare"} onValueChange={(v) => setDraft({ ...draft, stato: v })}>
                    <SelectTrigger data-testid="ddt-stato"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="da_verificare">Da verificare</SelectItem><SelectItem value="verificato">Verificato</SelectItem></SelectContent></Select>
                </div>
              </div>
              <div className="text-xs text-slate-500">{draft.righe_trovate !== undefined ? `${draft.righe_trovate} righe estratte dal PDF` : ""} · correggi liberamente qualsiasi valore</div>
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {(draft.rows || []).map((r, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`ddt-row-${i}`}>
                    <Input className="flex-1" placeholder="Articolo" value={r.articolo || ""} onChange={(e) => setRow(i, "articolo", e.target.value)} data-testid={`ddt-row-art-${i}`} />
                    <Input className="w-24" type="number" step="0.01" placeholder="Q.tà" value={r.quantita ?? ""} onChange={(e) => setRow(i, "quantita", e.target.value)} data-testid={`ddt-row-qta-${i}`} />
                    <Input className="w-24" type="number" step="0.01" placeholder="Prezzo" value={r.prezzo ?? ""} onChange={(e) => setRow(i, "prezzo", e.target.value)} />
                    <Input className="w-28" type="number" step="0.01" placeholder="Importo" value={r.importo ?? ""} onChange={(e) => setRow(i, "importo", e.target.value)} />
                    <button type="button" onClick={() => delRow(i)} className="p-2 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {(draft.rows || []).length === 0 && <div className="text-sm text-amber-700">Nessuna riga riconosciuta nel PDF: inseriscile manualmente.</div>}
              </div>
              <Button type="button" variant="outline" onClick={addRow} className="rounded-xl" data-testid="ddt-add-row"><Plus className="h-4 w-4 mr-1" /> Aggiungi riga</Button>
              <div><Label>Note</Label><Input value={draft.note || ""} onChange={(e) => setDraft({ ...draft, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Annulla</Button>
            <Button onClick={save} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="ddt-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LavanderiaDocs;
